import { useEffect, useRef, type ReactNode } from "react";

/**
 * Centred-glass-card layout shared by every auth screen (sign-in,
 * forgot-password, set-password / invite-accept). Ports the visual
 * treatment from Edge/Edge.Client/src/pages/login.tsx so the CF Pages
 * front door reads as one product with the Director SPA: animated
 * gradient strands behind a fixed dot-grid overlay behind a frosted
 * white card with the Optymyse wordmark centred above.
 *
 * Why animate in plain canvas rather than CSS keyframes: the strands
 * share colour, phase, amplitude, and frequency parameters that are
 * easier to orchestrate as one render loop than as 8 separate animated
 * elements. The canvas approach also lets us use composite modes
 * (`multiply` on light, `lighter` on dark) that CSS blend-mode can't
 * drive on a per-strand basis.
 */

/**
 * Tenant branding swap. When `branding` is present (locate resolved an
 * email to exactly one tenant with a logo configured), the shell crossfades
 * from the Optymyse wordmark to the customer's logo on the next render.
 * The `key` change forces a remount so the CSS `shell-logo-fade` keyframe
 * fires on the new element. M365 does the same swap on the password step
 * after submitting the email — see LOGIN-SECURITY.md for the trade-off.
 */
export interface ShellBranding {
    tenantName: string;
    logoUrl: string;
}

export function Shell({ title, subtitle, branding, children }: {
    title: string;
    subtitle?: string;
    branding?: ShellBranding;
    children: ReactNode;
}) {
    const logoSrc = branding ? branding.logoUrl : "/brand/optymyse-neg.svg";
    const logoAlt = branding ? branding.tenantName : "Optymyse";
    return (
        <main className="shell">
            <AnimatedBackground />
            <GridOverlay />
            <div className="shell-inner">
                <div className="shell-logo">
                    <img
                        key={logoSrc}
                        src={logoSrc}
                        alt={logoAlt}
                        className="shell-logo-img"
                    />
                </div>
                <div className="shell-card">
                    <header className="shell-heading">
                        <h1 className="shell-title">{title}</h1>
                        {subtitle && <p className="shell-subtitle">{subtitle}</p>}
                    </header>
                    {children}
                </div>
                <footer className="shell-footer">
                    <p className="shell-footer-line">
                        Optymyse Inspire &middot; Visual Communications
                    </p>
                    <p className="shell-footer-copy">
                        &copy; {new Date().getFullYear()} Optymyse Ltd. All rights
                        reserved.
                    </p>
                </footer>
            </div>
        </main>
    );
}

/**
 * Animated gradient-ribbon canvas. Ported verbatim from login.tsx —
 * strand constants, wave compositing, and isDark branching match so
 * the two apps produce the same motion. Cleans up its animation frame
 * on unmount so React Strict Mode double-mount in dev doesn't leak.
 */
function AnimatedBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        // JSDOM (test env) returns null for 2d contexts — short-circuit
        // so the animation loop is a no-op under test rather than
        // dumping stack traces about ctx.setTransform being undefined.
        const ctx = canvas.getContext("2d");
        if (!ctx || typeof ctx.setTransform !== "function") return;

        let animationId = 0;
        let time = 0;
        let ww = window.innerWidth;
        let wh = window.innerHeight;

        const resize = () => {
            ww = window.innerWidth;
            wh = window.innerHeight;
            canvas.width = ww * devicePixelRatio;
            canvas.height = wh * devicePixelRatio;
            canvas.style.width = ww + "px";
            canvas.style.height = wh + "px";
        };
        resize();
        window.addEventListener("resize", resize);

        // Flowing ribbons — each is a bezier spine with varying normals
        // rendered as a filled strip with a horizontal gradient fade.
        // Colour picks track the Optymyse palette (pompadour / stratos /
        // hibiscus / violet).
        const strands = [
            { yBase: 0.15, amplitude: 0.12, frequency: 1.2, speed: 0.4,  width: 180, color: [140, 40, 100],  phase: 0,   opacity: 0.5  },
            { yBase: 0.35, amplitude: 0.18, frequency: 0.8, speed: 0.3,  width: 220, color: [80,  60, 180],  phase: 1.5, opacity: 0.4  },
            { yBase: 0.55, amplitude: 0.14, frequency: 1.0, speed: 0.35, width: 160, color: [200, 60, 100],  phase: 3.0, opacity: 0.45 },
            { yBase: 0.75, amplitude: 0.16, frequency: 0.9, speed: 0.25, width: 200, color: [50,  50, 160],  phase: 4.5, opacity: 0.35 },
            { yBase: 0.25, amplitude: 0.20, frequency: 0.7, speed: 0.28, width: 240, color: [160, 30, 120],  phase: 2.0, opacity: 0.3  },
            { yBase: 0.65, amplitude: 0.10, frequency: 1.4, speed: 0.45, width: 140, color: [120, 50, 160],  phase: 5.0, opacity: 0.4  },
            { yBase: 0.45, amplitude: 0.22, frequency: 0.6, speed: 0.2,  width: 260, color: [180, 50, 80],   phase: 1.0, opacity: 0.25 },
            { yBase: 0.85, amplitude: 0.08, frequency: 1.6, speed: 0.5,  width: 120, color: [100, 40, 140],  phase: 3.5, opacity: 0.45 },
        ] as const;

        const draw = () => {
            time += 0.002;
            // CF Pages has no framework theme switcher — follow the
            // system preference directly. Matches what Edge does via
            // its Tailwind `.dark` class (the media query is the same
            // source of truth there too). Guard for JSDOM which doesn't
            // implement matchMedia.
            const isDark = typeof window.matchMedia === "function"
                ? window.matchMedia("(prefers-color-scheme: dark)").matches
                : false;

            ctx.save();
            ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

            ctx.fillStyle = isDark ? "#0f0a12" : "#faf7fb";
            ctx.fillRect(0, 0, ww, wh);

            // Ambient radial glows beneath the strands — just a soft
            // lift of brand hue out of the base, nothing structural.
            const ambient = [
                { x: 0.25, y: 0.3, r: 400, color: isDark ? "rgba(140,40,100,0.12)" : "rgba(140,40,100,0.08)" },
                { x: 0.75, y: 0.7, r: 350, color: isDark ? "rgba(80,60,180,0.10)"  : "rgba(80,60,180,0.06)"  },
            ];
            for (const orb of ambient) {
                const g = ctx.createRadialGradient(orb.x * ww, orb.y * wh, 0, orb.x * ww, orb.y * wh, orb.r);
                g.addColorStop(0, orb.color);
                g.addColorStop(1, "transparent");
                ctx.fillStyle = g;
                ctx.fillRect(0, 0, ww, wh);
            }

            // `multiply` on light lets the strands darken the off-white
            // base without going muddy; `lighter` on dark additively
            // stacks them so overlapping ribbons brighten instead of
            // cancelling.
            ctx.globalCompositeOperation = isDark ? "lighter" : "multiply";

            for (const strand of strands) {
                const steps = 120;
                const points: Array<{ x: number; y: number }> = [];

                // Build the strand's spine by sampling three overlaid
                // sin/cos waves at different frequencies. This is what
                // stops the motion from reading as a simple wave — the
                // third term breaks symmetry.
                for (let i = 0; i <= steps; i++) {
                    const t = i / steps;
                    const x = t * ww * 1.4 - ww * 0.2;
                    const baseY = strand.yBase * wh;
                    const w1 = Math.sin(t * Math.PI * 2 * strand.frequency + time * strand.speed + strand.phase) * strand.amplitude * wh;
                    const w2 = Math.sin(t * Math.PI * 3.7 * strand.frequency * 0.5 + time * strand.speed * 0.7 + strand.phase + 1) * strand.amplitude * wh * 0.3;
                    const w3 = Math.cos(t * Math.PI * 1.3 + time * strand.speed * 1.2 + strand.phase * 0.5) * strand.amplitude * wh * 0.15;
                    points.push({ x, y: baseY + w1 + w2 + w3 });
                }

                const halfWidth = strand.width * (ww / 1600);
                const [r, g, b] = strand.color;
                const alpha = isDark ? strand.opacity * 0.7 : strand.opacity * 1.2;

                // Ribbon edges: offset the spine along its normal at
                // each point, tapering towards the ends so the strands
                // fade out rather than clip.
                const top: Array<{ x: number; y: number }> = [];
                const bottom: Array<{ x: number; y: number }> = [];
                for (let i = 0; i < points.length; i++) {
                    const p = points[i]!;
                    const prev = points[Math.max(0, i - 1)]!;
                    const next = points[Math.min(points.length - 1, i + 1)]!;
                    const dx = next.x - prev.x;
                    const dy = next.y - prev.y;
                    const len = Math.sqrt(dx * dx + dy * dy) || 1;
                    const nx = -dy / len;
                    const ny = dx / len;
                    const taper = Math.sin((i / points.length) * Math.PI);
                    const w = halfWidth * taper * (0.8 + 0.2 * Math.sin(time * 0.5 + i * 0.05 + strand.phase));
                    top.push({ x: p.x + nx * w, y: p.y + ny * w });
                    bottom.push({ x: p.x - nx * w, y: p.y - ny * w });
                }

                ctx.beginPath();
                ctx.moveTo(top[0]!.x, top[0]!.y);
                for (let i = 1; i < top.length; i++) ctx.lineTo(top[i]!.x, top[i]!.y);
                for (let i = bottom.length - 1; i >= 0; i--) ctx.lineTo(bottom[i]!.x, bottom[i]!.y);
                ctx.closePath();

                // Horizontal gradient along each strand so it enters
                // and leaves the viewport faded instead of abruptly
                // ending at the edges.
                const grad = ctx.createLinearGradient(0, 0, ww, 0);
                grad.addColorStop(0, `rgba(${r},${g},${b},0)`);
                grad.addColorStop(0.2, `rgba(${r},${g},${b},${alpha * 0.6})`);
                grad.addColorStop(0.5, `rgba(${r},${g},${b},${alpha})`);
                grad.addColorStop(0.8, `rgba(${r},${g},${b},${alpha * 0.6})`);
                grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
                ctx.fillStyle = grad;
                ctx.filter = `blur(${Math.round(strand.width * 0.15)}px)`;
                ctx.fill();
                ctx.filter = "none";
            }

            ctx.globalCompositeOperation = "source-over";

            // Subtle film-grain pass — hides banding on wide gradients
            // on low-bit-depth screens.
            ctx.globalAlpha = isDark ? 0.025 : 0.015;
            ctx.fillStyle = isDark ? "#fff" : "#000";
            for (let i = 0; i < 600; i++) {
                ctx.fillRect(Math.random() * ww, Math.random() * wh, 1, 1);
            }
            ctx.globalAlpha = 1;

            ctx.restore();
            animationId = requestAnimationFrame(draw);
        };

        draw();

        return () => {
            cancelAnimationFrame(animationId);
            window.removeEventListener("resize", resize);
        };
    }, []);

    return <canvas ref={canvasRef} className="shell-bg-canvas" aria-hidden="true" />;
}

/** Subtle dot-grid overlay with radial fade mask. */
function GridOverlay() {
    return (
        <svg className="shell-bg-grid" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <defs>
                <pattern id="grid-login" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="20" cy="20" r="0.8" fill="currentColor" opacity="0.30" />
                </pattern>
                <radialGradient id="grid-fade" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="white" stopOpacity="1" />
                    <stop offset="70%" stopColor="white" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="white" stopOpacity="0" />
                </radialGradient>
                <mask id="grid-mask">
                    <rect width="100%" height="100%" fill="url(#grid-fade)" />
                </mask>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-login)" mask="url(#grid-mask)" />
        </svg>
    );
}
